// pattern: Imperative Shell
// Results view — shows quiz score and per-question summary

import type { StoredQuestion } from '../core/question-model.ts';
import { calculateScore } from '../core/quiz-engine.ts';
import { createElement, addListener } from '../shell/dom.ts';
import { navigate } from '../shell/router.ts';
import { saveAttempt, loadAttemptsForQuiz } from '../shell/storage.ts';

export type QuizResults = {
  readonly quizId: string;
  readonly quizName: string;
  readonly questions: ReadonlyArray<StoredQuestion>;
  readonly answers: ReadonlyArray<number>;
  readonly correctCount: number;
  readonly totalQuestions: number;
};

export function renderResults(container: HTMLElement, results: QuizResults): () => void {
  const cleanups: Array<() => void> = [];
  const score = calculateScore(results.correctCount, results.totalQuestions);

  // Save attempt to localStorage
  saveAttempt({
    quizId: results.quizId,
    score,
    correctCount: results.correctCount,
    totalQuestions: results.totalQuestions,
    completedAt: Date.now(),
  });

  const wrapper = createElement('div', { className: 'results-wrapper' });

  // Score card
  const scoreCard = createElement('div', { className: 'card results-score-card text-center' });

  const heading = createElement('h2', {
    textContent: 'Quiz Complete!',
    className: 'results-heading',
  });

  const scoreDisplay = createElement('div', { className: 'score-display' });

  const targetPercent = score;
  scoreDisplay.textContent = '0%';
  scoreDisplay.classList.add('score-counting');

  // Animate score count-up over ~1 second
  const duration = 1000;
  const startTime = performance.now();
  let scoreAnimationId: number | undefined;

  function animateScore(currentTime: number): void {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    // Ease-out curve for satisfying deceleration
    const eased = 1 - Math.pow(1 - progress, 3);
    const currentValue = Math.round(eased * targetPercent);
    scoreDisplay.textContent = `${currentValue}%`;

    if (progress < 1) {
      scoreAnimationId = requestAnimationFrame(animateScore);
    }
  }

  scoreAnimationId = requestAnimationFrame(animateScore);
  cleanups.push(() => {
    if (scoreAnimationId !== undefined) cancelAnimationFrame(scoreAnimationId);
  });

  const fraction = createElement('p', { className: 'text-secondary results-fraction' });
  fraction.textContent = `${results.correctCount} out of ${results.totalQuestions} correct`;

  scoreCard.append(heading, scoreDisplay, fraction);

  // Previous best score
  const previousAttempts = loadAttemptsForQuiz(results.quizId);
  if (previousAttempts.success && previousAttempts.data && previousAttempts.data.length > 1) {
    // Get best score from previous attempts (exclude current)
    const otherAttempts = previousAttempts.data.slice(0, -1);
    const bestPrevious = Math.max(...otherAttempts.map((a) => a.score));
    if (bestPrevious > 0) {
      const bestEl = createElement('p', { className: 'text-secondary results-best' });
      bestEl.textContent = `Previous best: ${bestPrevious}%`;
      scoreCard.appendChild(bestEl);
    }
  }

  wrapper.appendChild(scoreCard);

  // Question summary
  const summaryHeading = createElement('h3', {
    textContent: 'Question Summary',
    className: 'results-summary-heading',
  });
  wrapper.appendChild(summaryHeading);

  const summaryList = createElement('div', { className: 'question-summary results-summary-list' });

  const LETTERS = ['A', 'B', 'C', 'D'] as const;

  results.questions.forEach((q, i) => {
    const answer = results.answers[i];
    const isCorrect = answer === q.correctAnswerIndex;

    const row = createElement('div', { className: 'summary-row' });

    const icon = createElement('span');
    icon.textContent = isCorrect ? '\u2713' : '\u2717';
    icon.className = `summary-icon ${isCorrect ? 'summary-icon-correct' : 'summary-icon-wrong'}`;

    const textDiv = createElement('div', { className: 'summary-text' });

    if (q.figureDataUrl) {
      const thumbDiv = createElement('div', { className: 'question-figure-thumbnail' });
      const thumbImg = createElement('img') as HTMLImageElement;
      thumbImg.src = q.figureDataUrl;
      thumbImg.alt = 'Question figure';
      thumbDiv.appendChild(thumbImg);
      textDiv.appendChild(thumbDiv);
    }

    const questionText = createElement('p', { className: 'summary-question-text' });
    questionText.textContent = `${i + 1}. ${q.question}`;
    textDiv.appendChild(questionText);

    if (!isCorrect && answer !== undefined) {
      const correctInfo = createElement('p', { className: 'text-secondary summary-correct-info' });
      correctInfo.textContent = `Correct answer: ${LETTERS[q.correctAnswerIndex]}) ${q.options[q.correctAnswerIndex]}`;
      textDiv.appendChild(correctInfo);
    }

    row.append(icon, textDiv);
    summaryList.appendChild(row);
  });

  wrapper.appendChild(summaryList);

  // Action buttons
  const actions = createElement('div', { className: 'results-actions' });

  const retakeBtn = createElement('button', {
    className: 'btn btn-primary results-action-btn',
    textContent: 'Retake Quiz',
  });
  const retakeCleanup = addListener(retakeBtn, 'click', () => {
    navigate(`#/q/${results.quizId}`);
  });
  cleanups.push(retakeCleanup);

  const homeBtn = createElement('button', {
    className: 'btn btn-secondary results-action-btn',
    textContent: 'Go Home',
  });
  const homeCleanup = addListener(homeBtn, 'click', () => {
    navigate('#/');
  });
  cleanups.push(homeCleanup);

  actions.append(retakeBtn, homeBtn);
  wrapper.appendChild(actions);

  container.appendChild(wrapper);

  return () => {
    cleanups.forEach((fn) => fn());
  };
}
